import React, { useCallback, useContext, useEffect } from 'react';

// DEPENDENCIES
import isString from 'lodash/isString';
import orderBy from 'lodash/orderBy';

// GLOBAL VARIABLES
import { ENDPOINTS } from 'endpoints.js';
import { INTERFACE, RECORD } from 'pathnames.js';

// GLOBACL FUNCTIONS
import { makePath } from 'functions.js';

// GLOBAL HELPERS
import { getListData, ListContext } from 'helpers/getListData';

// PARTS
import Main from 'parts/main/Main';
import Table from 'parts/table/Table.js';

// LOCAL VARIABLES
import { ACTIONS, COLUMNS } from '../../../columns.js';
import SearchCollapse from './search/tools/Collapse.js';

// MAIN COMPONENT
const Results = () => {
  // CONTEXT
  const {
    data: { deal = {}, inventory = {} },
    preferences: { filters } = {},
    updateContext
  } = useContext(ListContext) || {};


  // Search again when filters change
  useEffect(() => {
    updateContext({ type: 'updateSettings', reload: true });
  }, [filters, updateContext]);

  const handleSort = useCallback(
    (data, keyParam, direction) => {
      let iteratee;
      switch (keyParam) {
        case 'age':
          iteratee = (item) => {
            const value = isString(item.age) ? +item.age.replace(',', '') : item.age;
            return isNaN(value) ? 0 : value;
          };
          break;
        case 'mileage':
          iteratee = (item) => {
            const value = isString(item.mileage) ? +item.mileage.replace(',', '') : item.mileage;
            return isNaN(value) ? 0 : value;
          };
          break;
        case 'trades':
          iteratee = (item) => [
            item.trades?.[0]?.vehicle_year || 0, 
            item.trades?.[0]?.make?.toLowerCase() || ''
          ];
          break;
        default:
          iteratee = (item) => {
            const value = item[keyParam];
            return isString(value) ? value.toLowerCase() : value;
          };
      }
      return orderBy(data, iteratee, direction);
    },
    []
  );

  // RENDER
  return (
    <Main>
      <SearchCollapse title="Inventory Search" total={inventory.total_records}>
        <Table
          result={inventory.records}
          columns={[
            {
              ...COLUMNS.purchasedDate,
              key: 'purchase_date'
            },
            {
              ...COLUMNS.inventorySource,
              key: 'inventory_type'
            },
            {
              ...COLUMNS.ageOfVehicle,
              key: 'age'
            },
            COLUMNS.stockNumber,
            {
              ...COLUMNS.makeModel,
              key: 'vehicle_info'
            },
            COLUMNS.mileage,
            COLUMNS.acv,
            COLUMNS.dmsInvoice,
            {
              key: 'inventory_source',
              label: 'SRC',
              tooltip: 'Inventory Source'
            },
            {
              ...COLUMNS.inventoryStatus,
              width: 55
            }
          ]}
          showNumber
          mobilePreviewKeys={['stock_num', 'purchase_date', 'vehicle_info']}
          autoSizeHeight={false}
          onSort={handleSort}
        />
      </SearchCollapse>
      <SearchCollapse title="Deal Search" total={deal.total_records}>
        <Table
          result={deal.records}
          columns={[
            {
              component: Table.Date,
              key: 'sales_time',
              label: 'Date',
              tooltip: 'Time when the current Deal Status was entered',
              width: 65
            },
            {
              ...COLUMNS.salesPerson,
              key: 'sales_man_name'
            },
            {
              ...COLUMNS.customer(),
              key: 'customer_full_name'
            },
            {
              ...COLUMNS.ageOfVehicle,
              key: 'age'
            },
            COLUMNS.newUsed,
            {
              ...COLUMNS.purchaseLease,
              key: 'sale_type_code'
            },
            {
              ...COLUMNS.retailWholesale,
              key: 'sale_type_category_code'
            },
            COLUMNS.stockNumber,
            {
              ...COLUMNS.makeModel,
              key: 'vehicle_info'
            },
            {
              ...COLUMNS.trades,
              key: 'trades',
            },
            {
              ...COLUMNS.frontendGross,
              key: 'frt_gross'
            },
            COLUMNS.amGross,
            COLUMNS.fiGross,
            {
              ...COLUMNS.totalGross,
              label: 'TTL Gross',
              width: 95
            }
          ]}
          showNumber
          mobilePreviewKeys={['customer_full_name', 'vehicle_info', 'stock_num']}
          autoSizeHeight={false}
          onSort={handleSort}
        />
      </SearchCollapse>
    </Main>
  );
};

const handleRedirect = (code, saleID, inventoryID) => {
  if (code && saleID) {
    if (code === 'S' || code === 'O') {
      return makePath(INTERFACE.amFinance, RECORD.view, saleID);
    }
    if (code === 'F') {
      return makePath(INTERFACE.fallout, RECORD.view, saleID);
    }

    return makePath(INTERFACE.sales, RECORD.view, saleID);
  }

  return makePath(INTERFACE.inventory, RECORD.view, inventoryID);
};

// EXPORT
export default getListData(Results, {
  key: 'search',
  fetchArgs: {
    endpoint: ENDPOINTS.globalSearch.search
  },
  defaults: {
    startRow: null
  },
  actions: [
    {
      ...ACTIONS.open(),
      to: ({ sales_status_code, sales_id, daily_sales_inventory_id }) =>
        handleRedirect(sales_status_code, sales_id, daily_sales_inventory_id)
    }
  ]
});
